"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Regulation() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "R22 PHASE-OUT",
    icon: Blocks,
    description: "The R22 refrigerant, once standard, is now strictly regulated due to its ozone-depleting properties. We specialize in the safe decommissioning and replacement of legacy R22 systems, ensuring your facility meets modern environmental compliance standards without operational downtime."
  }, {
    number: "02",
    title: "COMPLIANCE AUDITS",
    icon: BrainCircuit,
    description: "Our technical team conducts thorough site assessments to identify non-compliant climate control hardware. We provide detailed reporting and strategic roadmaps to transition your infrastructure to eco-friendly, high-efficiency refrigerants that align with current UK legislation."
  }, {
    number: "03",
    title: "SYSTEM UPGRADES",
    icon: Lock,
    description: "Future-proof your climate control with our advanced, sustainable cooling solutions. We install high-performance, energy-efficient systems that offer superior reliability and lower operational costs, ensuring your business remains fully compliant and climate-ready for years to come."
  }];
  return <section data-section-id="5200" id="r22regulationnotice" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-default text-xs md:text-sm tracking-widest uppercase text-primary">
                02 / REGULATORY COMPLIANCE
              </span>
              <div className="h-px w-16 bg-primary" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-black text-4xl sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tight uppercase">
              R22 REFRIGERANT /
              <br />
              LEGAL REQUIREMENTS.
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-slate-50 backdrop-blur-sm border border-slate-200 rounded-lg p-10 md:p-12 flex flex-col shadow-md hover:border-primary hover:shadow-lg transition-all duration-500">
        {}
        <div className="font-default text-5xl md:text-6xl font-light text-primary transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-slate-200 mt-6 mb-10 group-hover:bg-primary transition-colors duration-500" />

        {}
        <Icon className="size-8 text-slate-500 group-hover:text-primary transition-colors duration-500 mb-8" strokeWidth={1} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight">
          {pillar.title}
        </h3>

        {}
        <p className="font-default text-base md:text-[17px] leading-relaxed text-slate-600 mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        <div className="mt-auto">
          <Button variant="ghost" size="sm" className="font-default text-xs tracking-widest uppercase text-slate-900 hover:text-primary hover:bg-transparent p-0 h-auto group/btn">
            VIEW COMPLIANCE GUIDE
            <ArrowUpRight className="size-4 text-primary transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
          </Button>
        </div>
      </Card>
    </div>;
}